import { cn } from '@/lib/cn';
import { Icono } from '@/components/Icono';
import type { FilaDeRanking, Reconocimiento, Valor, ValorContado } from './api';

/**
 * piezas.tsx — las partes pequeñas de Reconocimientos.
 *
 * Están juntas porque ninguna se usa fuera de esta feature. En cuanto una haga
 * falta en otra pantalla, sube a `components/` — una feature no importa de otra.
 * Eso le pasó a `Icono` el 2026-09-24: lo necesitaba la Analítica, así que vive
 * en `components/Icono.tsx` y aquí solo se reexporta para no tocar a sus tres
 * consumidores de esta feature.
 */

export { Icono };

/* ── El podio ──────────────────────────────────────────────────────────── */

// Oro, plata y bronce. Son los únicos hexadecimales de esta feature, y se
// quedan escritos a mano a propósito: no son colores del sistema de diseño,
// son la convención universal de un podio. Convertirlos en tokens los ataría
// al tema de la marca, y entonces cambiar el color corporativo cambiaría el
// color del primer puesto, que no tiene ninguna relación.
//
// Contraste medido el 2026-09-10 con la tinta `--navy` encima: 7,04 / 6,87 /
// 4,53. El bronce es el que va justo, y por eso está aquí anotado — si alguien
// lo oscurece, baja de 4,5.
const PODIO = ['#F5A623', '#ADB5BD', '#cd7f32'];

export function Puesto({ n }: { n: number }) {
  const fondo = PODIO[n - 1];
  return (
    <div
      className={cn(
        'flex size-7 shrink-0 items-center justify-center rounded-full',
        'text-3 font-bold text-brand-ink',
        !fondo && 'bg-sunken',
      )}
      style={fondo ? { background: fondo } : undefined}
    >
      {n}
    </div>
  );
}

export function FilaDelRanking({ fila, puesto }: { fila: FilaDeRanking; puesto: number }) {
  return (
    <div className="flex items-center gap-3 border-b border-line py-2 last:border-0">
      <Puesto n={puesto} />
      {/* `min-w-0` para que un nombre largo se recorte en vez de empujar el
          contador fuera de la tarjeta. */}
      <div className="min-w-0 flex-1 truncate text-3 font-semibold">
        {fila.full_name || 'Usuario'}
      </div>
      <span className="shrink-0 font-bold text-warn-ink">
        {fila.total_kudos} <i className="bi bi-star-fill" aria-hidden="true" />
      </span>
    </div>
  );
}

/* ── La barra de un valor ──────────────────────────────────────────────── */

/** Cuántas veces se reconoció cada valor.
 *
 *  La barra crece con `transform`, no con `width`. Es la misma corrección que
 *  el proyecto lleva haciendo desde el 2026-09-01: animar `width` obliga al
 *  navegador a rehacer la maqueta en cada fotograma, y con quince valores en la
 *  lista eso se nota en un equipo modesto. `Utils.barChart`, el equivalente
 *  vanilla, se corrigió igual el mismo día. */
export function BarraDeValor({ valor, maximo }: { valor: ValorContado; maximo: number }) {
  const parte = Math.max(valor.count / Math.max(maximo, 1), 0.02);
  return (
    <div className="mb-2 flex items-center gap-2 last:mb-0">
      <span className="w-6 text-center text-5">
        <Icono nombre={valor.icon} respaldo="bi-star" />
      </span>
      <span className="w-32 shrink-0 truncate text-3">{valor.name}</span>
      <div className="h-3.5 flex-1 overflow-hidden rounded-sm bg-sunken">
        <div
          className="h-full origin-left transition-transform duration-500 motion-reduce:transition-none"
          style={{
            transform: `scaleX(${parte})`,
            background: valor.color || 'var(--orange)',
          }}
        />
      </div>
      <strong className="w-7 shrink-0 text-right text-2 text-brand-ink">{valor.count}</strong>
    </div>
  );
}

/* ── La tarjeta de un reconocimiento ───────────────────────────────────── */

export function TarjetaDeReconocimiento({
  kudo, valor,
}: { kudo: Reconocimiento; valor?: Valor }) {
  const nombreDelValor = valor?.name || kudo.badge;
  return (
    <div className="kudo-card fade-in">
      <div className="kudo-badge-icon">
        <Icono nombre={valor?.icon} respaldo="bi-star" />
      </div>
      <div className="font-bold text-brand-ink">{nombreDelValor}</div>
      {kudo.message && (
        <p className="mt-1.5 text-3 italic text-muted">«{kudo.message}»</p>
      )}
      <div className="kudo-from">
        <i
          className={cn('bi', kudo.to_company ? 'bi-building' : 'bi-arrow-right-circle')}
          aria-hidden="true"
        />{' '}
        {/* Los tres nombres de esta tarjeta son nodos de texto. En el vanilla
            eran interpolaciones sin escapar que `test_escapado` no veía, porque
            su patrón no reconoce la forma `x.sender_name || 'Bienestar'`. */}
        <strong>{kudo.sender_name || 'Bienestar'}</strong> reconoció a{' '}
        <strong>{kudo.to_company ? 'toda la empresa' : kudo.receiver_name || 'Usuario'}</strong>
      </div>
      <time className="mt-1 block text-2 text-muted" dateTime={kudo.created_at}>
        {Utils.relativeTime(kudo.created_at)}
      </time>
    </div>
  );
}
